import {isLoggedIn} from '@github-ui/client-env'
import {CodingAssistantButton, CodingAssistantMenuItems} from '@github-ui/coding-assistant-button'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useIssueState} from '@github-ui/issue-pr-state'
import {getIssueStateIcon} from '@github-ui/list-view-items-issues-prs/StateIcon'
import {ChevronRightIcon, KebabHorizontalIcon, LinkIcon, PlusIcon, TerminalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, SkeletonBox} from '@primer/react'
import {clsx} from 'clsx'
import React, {Suspense, useCallback, useEffect, useMemo, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import {BUTTON_LABELS} from '../../constants/buttons'
import {TEST_IDS} from '../../constants/test-ids'
import styles from '../IssueViewer.module.css'
import type {OptionConfig} from '../OptionConfig'
import {StickySubscribeButton} from '../sections/subscription-section/StickySubscribeButton'
import type {LinkedSecurityAlertsFragment$key} from './__generated__/LinkedSecurityAlertsFragment.graphql'
import type {StickyHeader$key} from './__generated__/StickyHeader.graphql'
import {CreateIssueButtonShell} from './CreateIssueButtonShell'
import type {HeaderBlockedBySummaryFragment} from './HeaderBlockedBySummary'
import type {HeaderSubIssueSummaryFragment} from './HeaderSubIssueSummary'
import {StickyHeaderChips} from './StickyHeaderChips'
import type {TrackedByFragment} from './TrackedBy'
import {ViewPositionIndicator} from './ViewPositionIndicator'

const StickyHeaderCreateIssueButtonLazy = React.lazy(async () => {
  const m = await import('@github-ui/issue-create/CreateIssueButton')
  return {default: m.CreateIssueButton}
})

export type StickyHeaderProps = {
  issueKey: StickyHeader$key | null | undefined
  secondaryIssueDataKey?: LinkedSecurityAlertsFragment$key
  blockedBySecondaryData?: HeaderBlockedBySummaryFragment
  trackedBySecondaryData?: TrackedByFragment
  subIssueSecondaryData?: HeaderSubIssueSummaryFragment
  issueNumber: number
  ownerLogin: string
  repoName: string
  issueUrl: string
  optionConfig: OptionConfig
  isHeaderSticky: boolean
}

export function StickyHeader({issueKey, ...rest}: StickyHeaderProps) {
  if (!issueKey) {
    return <StickyHeaderSkeleton optionConfig={rest.optionConfig} />
  }
  return <StickyHeaderContent {...rest} issueKey={issueKey} />
}

function NavControls({optionConfig}: {optionConfig: OptionConfig}) {
  if (!optionConfig.viewNavState) return null
  return (
    <>
      <div className={styles.topContainerNavControls}>
        <ViewPositionIndicator
          navigate={optionConfig.navigate}
          viewNavState={optionConfig.viewNavState}
          onLoadMore={optionConfig.onLoadMoreNavItems}
          onLoadBack={optionConfig.onLoadBackNavItems}
          pendingFocusDirection={optionConfig.viewNavState?.pendingFocusDirection}
          pendingNavSource={optionConfig.viewNavState?.pendingNavSource}
          isStickyHeader
        />
      </div>
      <span aria-hidden="true" className={styles.topContainerExtrasDivider} />
    </>
  )
}

function StickyHeaderSkeleton({optionConfig}: {optionConfig: OptionConfig}) {
  return (
    <div data-testid={TEST_IDS.issueViewerTopContainer} className={styles.topContainer}>
      <div className={styles.topContainerLeading}>
        <NavControls optionConfig={optionConfig} />
        <SkeletonBox width="180px" height="20px" />
      </div>
      <div className={styles.topContainerExtras} aria-hidden="true">
        <div className={styles.topContainerActionGroup}>
          <SkeletonBox width="24px" height="24px" />
          <SkeletonBox width="24px" height="24px" />
          <SkeletonBox width="24px" height="24px" />
        </div>
      </div>
    </div>
  )
}

type StickyHeaderContentProps = StickyHeaderProps & {issueKey: StickyHeader$key}

function StickyHeaderContent({
  issueKey,
  secondaryIssueDataKey,
  blockedBySecondaryData,
  trackedBySecondaryData,
  subIssueSecondaryData,
  issueNumber,
  ownerLogin,
  repoName,
  issueUrl,
  optionConfig,
  isHeaderSticky,
}: StickyHeaderContentProps) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment StickyHeader on Issue {
        state
        stateReason
        title
        repository {
          isWritable
        }
        ...StickyHeaderChips
        id
        threadSubscriptionChannel
        ...StickySubscribeButtonRefetchableFragment
      }
    `,
    issueKey,
  )

  const {stateString} = useIssueState({
    state: data.state,
    stateReason: data.stateReason,
    options: {longText: false},
  })
  const stateOrReason =
    data.state === 'CLOSED' && (data.stateReason === 'NOT_PLANNED' || data.stateReason === 'DUPLICATE')
      ? data.stateReason
      : data.state
  const {icon: StateIcon, color: stateIconColor} = getIssueStateIcon(stateOrReason)

  const codingAssistantEnabled = isFeatureEnabled('issue_cca_modal_open')

  // At narrow viewports collapse the bell/copy/new-issue actions into a single
  // kebab menu; above the breakpoint we keep the three individual buttons.
  const [isNarrow, setIsNarrow] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia('(max-width: 1024px)')
    const update = () => setIsNarrow(mql.matches)
    update()
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [])

  const showCreateButton = optionConfig.showIssueCreateButton && data.repository.isWritable

  const handleCopyLink = useCallback(() => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(issueUrl)
    }
  }, [issueUrl])

  const assistantPrompt = useMemo(() => `Work on the following GitHub issue: ${issueUrl}.`, [issueUrl])

  return (
    <div data-testid={TEST_IDS.issueViewerTopContainer} className={styles.topContainer}>
      <div className={styles.topContainerLeading}>
        <NavControls optionConfig={optionConfig} />
        <span style={{color: stateIconColor, display: 'inline-flex'}}>
          <StateIcon aria-label={stateString} />
        </span>
        <span className={styles.topContainerIssueNumber}>#{issueNumber}</span>
        <a
          href="#top"
          className={clsx(styles.topContainerTitle, !isHeaderSticky && styles.topContainerTitleHidden)}
          aria-hidden={!isHeaderSticky}
        >
          {data.title}
        </a>
        <div
          className={clsx(styles.topContainerChipsSlot, !isHeaderSticky && styles.topContainerChipsSlotHidden)}
          aria-hidden={!isHeaderSticky}
        >
          <StickyHeaderChips
            issueKey={data}
            secondaryIssueDataKey={secondaryIssueDataKey}
            blockedBySecondaryData={blockedBySecondaryData}
            trackedBySecondaryData={trackedBySecondaryData}
            subIssueSecondaryData={subIssueSecondaryData}
            optionConfig={optionConfig}
          />
        </div>
      </div>
      <div className={styles.topContainerExtras} aria-hidden={!isHeaderSticky}>
        {isHeaderSticky && isNarrow ? (
          <>
            {isLoggedIn() && (
              <span className={styles.topContainerOverflowSlot}>
                <ActionMenu>
                  <ActionMenu.Anchor>
                    <IconButton
                      icon={KebabHorizontalIcon}
                      aria-label={BUTTON_LABELS.moreActions}
                      variant="invisible"
                      size="medium"
                    />
                  </ActionMenu.Anchor>
                  <ActionMenu.Overlay>
                    <ActionList>
                      <StickySubscribeButton issue={data} subscriptionData={data} variant="menu-item" />
                      <ActionList.Item onSelect={handleCopyLink}>
                        <ActionList.LeadingVisual>
                          <LinkIcon />
                        </ActionList.LeadingVisual>
                        {BUTTON_LABELS.copyIssueLink}
                      </ActionList.Item>
                      {showCreateButton && (
                        <ActionList.LinkItem href={`/${ownerLogin}/${repoName}/issues/new`}>
                          <ActionList.LeadingVisual>
                            <PlusIcon />
                          </ActionList.LeadingVisual>
                          {BUTTON_LABELS.new}
                        </ActionList.LinkItem>
                      )}
                      {codingAssistantEnabled && (
                        <ActionMenu>
                          <ActionMenu.Anchor>
                            <ActionList.Item>
                              <ActionList.LeadingVisual>
                                <TerminalIcon />
                              </ActionList.LeadingVisual>
                              {BUTTON_LABELS.openIn}
                              <ActionList.TrailingVisual>
                                <ChevronRightIcon />
                              </ActionList.TrailingVisual>
                            </ActionList.Item>
                          </ActionMenu.Anchor>
                          <ActionMenu.Overlay>
                            <CodingAssistantMenuItems prompt={assistantPrompt} hideOpenInHeading />
                          </ActionMenu.Overlay>
                        </ActionMenu>
                      )}
                    </ActionList>
                  </ActionMenu.Overlay>
                </ActionMenu>
              </span>
            )}
            {!isLoggedIn() && (
              <span className={styles.topContainerOverflowSlot}>
                <CopyToClipboardButton
                  textToCopy={issueUrl}
                  ariaLabel={BUTTON_LABELS.copyIssueLink}
                  icon={LinkIcon}
                  variant="invisible"
                  tooltipProps={{direction: 's'}}
                />
              </span>
            )}
          </>
        ) : (
          <div className={styles.topContainerActionGroup}>
            {isLoggedIn() && (
              <span className={styles.topContainerOverflowSlot}>
                <StickySubscribeButton issue={data} subscriptionData={data} />
              </span>
            )}
            <span className={styles.topContainerOverflowSlot}>
              <CopyToClipboardButton
                textToCopy={issueUrl}
                ariaLabel={BUTTON_LABELS.copyIssueLink}
                icon={LinkIcon}
                variant="invisible"
                tooltipProps={{direction: 's'}}
              />
            </span>
            {showCreateButton && (
              <span className={clsx(styles.topContainerOverflowSlot, styles.topContainerNewSlot)}>
                <Suspense fallback={<CreateIssueButtonShell iconOnly />}>
                  <StickyHeaderCreateIssueButtonLazy
                    label={BUTTON_LABELS.new}
                    variant="invisible"
                    iconOnly
                    leadingVisual={PlusIcon}
                    navigate={optionConfig.navigate}
                    optionConfig={{
                      showRepositoryPicker: false,
                      issueCreateArguments: {
                        repository: {
                          owner: ownerLogin,
                          name: repoName,
                        },
                      },
                      showFullScreenButton: true,
                      navigateToFullScreenOnTemplateChoice: true,
                      canBypassTemplateSelection: true,
                      navigate: optionConfig.navigate,
                    }}
                  />
                </Suspense>
              </span>
            )}
            {codingAssistantEnabled && (
              <div className={styles.topContainerActions}>
                <span aria-hidden="true" className={styles.topContainerActionsDivider} />
                <CodingAssistantButton
                  url={issueUrl}
                  buttonLabel={BUTTON_LABELS.openIn}
                  promptBuilder={(url: string) => `Work on the following GitHub issue: ${url}.`}
                  size="medium"
                  variant="invisible"
                  iconOnly
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
